/**
 * Yandex Cabinet API client
 */
import axios, { AxiosInstance } from 'axios';

const API_BASE_URL =
  import.meta.env.VITE_API_URL !== undefined && import.meta.env.VITE_API_URL !== null
    ? String(import.meta.env.VITE_API_URL)
    : 'http://localhost:3000';

const SESSION_USER_KEY = 'yandexUser';
const SESSION_TOKEN_KEY = 'yandexToken';
const SESSION_ROLES_KEY = 'yandexCompanyRoles';
const SESSION_COMPANY_KEY = 'yandexCompanyId';
const SESSION_LANG_KEY = 'yandexUiLang';

export interface AuthUser {
  id: string;
  name: string;
  email?: string | null;
  iin?: string | null;
}

export interface CompanyRole {
  company_id: string | null;
  role: string;
  company_name?: string;
  bin?: string;
}

export interface LoginResponse {
  message: string;
  user: AuthUser;
  companyRoles: CompanyRole[];
  isSuperadmin: boolean;
  isYandex: boolean;
  token: string;
}

export interface Document {
  id: string;
  companyId?: string;
  yandexId?: string;
  driverId?: string;
  contractorIin?: string;
  status: string;
  documentType?: string;
  category?: string;
  source?: 'yandex' | 'park';
  avrSide?: 'yandex' | 'driver';
  priceTotal?: number;
  sellerIin?: string;
  sellerName?: string;
  sellerPhone?: string;
  buyerIin?: string;
  buyerName?: string;
  buyerPhone?: string;
  fio?: string;
  iin?: string;
  metadata?: Record<string, any>;
  createdAt: string;
  updatedAt?: string;
  pdfBase64?: string;
  signedPdfBase64?: string;
  signatureMarks?: {
    DRIVER?: boolean;
    ACCOUNTANT?: boolean;
    YANDEX?: boolean;
  };
  waitingKey?: string;
  statusLabel?: string;
  driverPaywall?: {
    blocked?: boolean;
    message?: string;
    amount?: number;
    period?: string;
  } | null;
  signatures?: Array<{
    id: string;
    signerType: string;
    signedAt?: string;
    certificateSubject?: string;
    signatureValid?: boolean | null;
  }>;
}

export interface YandexUserRow {
  id: string;
  name: string;
  email?: string | null;
  iin?: string | null;
  is_active: boolean;
  created_at?: string;
  role_created_at?: string;
}

export interface YandexCompanyProfile {
  id: string;
  name: string;
  bin: string;
  status: string;
  address?: string | null;
}

export interface ApiClientRow {
  id: string;
  companyId?: string | null;
  name: string;
  keyPrefix: string;
  scope?: string;
  isActive: boolean;
  lastUsedAt?: string | null;
  createdAt?: string;
}

class YandexApiService {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: `${API_BASE_URL}/api/v1`,
      timeout: 120000,
      headers: { 'Content-Type': 'application/json' },
    });

    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem(SESSION_TOKEN_KEY);
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      const user = this.getStoredUser();
      if (user?.id) {
        config.headers['x-user-id'] = user.id;
      }
      const companyId = this.getSelectedCompanyId();
      if (companyId) {
        config.headers['x-company-id'] = companyId;
      }
      return config;
    });
  }

  getStoredUser(): AuthUser | null {
    try {
      const raw = localStorage.getItem(SESSION_USER_KEY);
      return raw ? (JSON.parse(raw) as AuthUser) : null;
    } catch {
      return null;
    }
  }

  getStoredToken(): string | null {
    return localStorage.getItem(SESSION_TOKEN_KEY);
  }

  getCompanyRoles(): CompanyRole[] {
    try {
      const raw = localStorage.getItem(SESSION_ROLES_KEY);
      return raw ? (JSON.parse(raw) as CompanyRole[]) : [];
    } catch {
      return [];
    }
  }

  getSelectedCompanyId(): string | null {
    return localStorage.getItem(SESSION_COMPANY_KEY);
  }

  setSelectedCompanyId(companyId: string | null) {
    if (companyId) localStorage.setItem(SESSION_COMPANY_KEY, companyId);
    else localStorage.removeItem(SESSION_COMPANY_KEY);
  }

  getUiLang(): string {
    const raw = localStorage.getItem(SESSION_LANG_KEY) || 'ru';
    return raw === 'kk' ? 'kk' : 'ru';
  }

  publicSignUrl(documentId: string): string {
    const api = String(API_BASE_URL || '').replace(/\/$/, '');
    const origin = api.replace(/\/api\/v1$/i, '');
    const base =
      origin && /^https?:\/\//i.test(origin)
        ? origin
        : typeof window !== 'undefined'
          ? window.location.origin
          : origin;
    return `${String(base).replace(/\/$/, '')}/sign/${documentId}`;
  }

  setUiLang(lang: string) {
    localStorage.setItem(SESSION_LANG_KEY, lang);
  }

  isLoggedIn(): boolean {
    return Boolean(this.getStoredUser() && this.getStoredToken());
  }

  setSession(user: AuthUser, token: string, companyRoles: CompanyRole[] = []) {
    localStorage.setItem(SESSION_USER_KEY, JSON.stringify(user));
    localStorage.setItem(SESSION_TOKEN_KEY, token);
    localStorage.setItem(SESSION_ROLES_KEY, JSON.stringify(companyRoles));
    const first =
      companyRoles.find((r) => r.company_id)?.company_id ||
      companyRoles[0]?.company_id ||
      null;
    if (first && !this.getSelectedCompanyId()) {
      this.setSelectedCompanyId(first);
    }
  }

  clearSession() {
    localStorage.removeItem(SESSION_USER_KEY);
    localStorage.removeItem(SESSION_TOKEN_KEY);
    localStorage.removeItem(SESSION_ROLES_KEY);
    localStorage.removeItem(SESSION_COMPANY_KEY);
  }

  async login(email: string, password: string): Promise<LoginResponse> {
    const response = await this.client.post<LoginResponse>('/auth/login', { email, password });
    const data = response.data;
    if (!data.isYandex) {
      throw new Error('Нет доступа к кабинету');
    }
    this.setSession(data.user, data.token, data.companyRoles || []);
    return data;
  }

  /** Login via NCALayer: challenge → sign(dataBase64) → login */
  async loginWithNcalayer(
    signFn: (dataBase64: string) => Promise<string>
  ): Promise<LoginResponse> {
    const challenge = await this.client.post<{
      challengeId: string;
      dataBase64: string;
    }>('/auth/ncalayer/challenge');
    const { challengeId, dataBase64 } = challenge.data;
    if (!challengeId || !dataBase64) {
      throw new Error('Не удалось получить challenge для ЭЦП');
    }
    const signatureBase64 = await signFn(dataBase64);
    if (!signatureBase64) {
      throw new Error('Пустая подпись');
    }
    const response = await this.client.post<LoginResponse>('/auth/ncalayer/login', {
      challengeId,
      signatureBase64,
    });
    const data = response.data;
    if (!data.isYandex) {
      throw new Error('Нет доступа к кабинету Яндекса');
    }
    this.setSession(data.user, data.token, data.companyRoles || []);
    return data;
  }

  async getDocuments(opts: {
    direction?: 'outgoing' | 'incoming';
    status?:
      | 'ALL'
      | 'NEW'
      | 'IN_PROGRESS'
      | 'COMPLETED'
      | 'OVERDUE'
      | 'CANCELLED'
      | 'REJECTED';
    companyId?: string;
  } = {}) {
    const params = new URLSearchParams();
    if (opts.direction) params.set('direction', opts.direction);
    params.set('status', opts.status || 'ALL');
    if (opts.companyId) params.set('company_id', opts.companyId);
    const qs = params.toString();
    const response = await this.client.get<{
      count: number;
      documents: Document[];
      direction?: string;
    }>(`/yandex/documents${qs ? `?${qs}` : ''}`);
    return response.data;
  }

  async getSigningControl(opts: {
    period?: string;
    status?: string;
    direction?: 'all' | 'incoming' | 'outgoing';
    yandexSigned?: 'all' | 'yes' | 'no';
    parkSigned?: 'all' | 'yes' | 'no';
    q?: string;
  } = {}) {
    const params = new URLSearchParams();
    if (opts.period) params.set('period', opts.period);
    if (opts.status) params.set('status', opts.status);
    if (opts.direction && opts.direction !== 'all') params.set('direction', opts.direction);
    if (opts.yandexSigned && opts.yandexSigned !== 'all') params.set('yandexSigned', opts.yandexSigned);
    if (opts.parkSigned && opts.parkSigned !== 'all') params.set('parkSigned', opts.parkSigned);
    if (opts.q) params.set('q', opts.q);
    const qs = params.toString();
    const response = await this.client.get<{
      period: string | null;
      stats: {
        people: number;
        waitingDriver: number;
        yandex: { total: number; pending: number; signed: number; overdue: number };
        park: { total: number; pending: number; signed: number; overdue: number };
        whatsapp: { sent: number; failed: number; skipped: number; none: number };
      };
      rows: Array<{
        iin: string;
        fio: string;
        phone: string;
        kind?: 'park' | 'direct';
        kindLabel?: string;
        yandexDriverSigned?: boolean | null;
        parkDriverSigned?: boolean | null;
        yandex: {
          count: number;
          status: string | null;
          statusLabel: string;
          documentId: string | null;
        };
        park: {
          count: number;
          status: string | null;
          statusLabel: string;
          documentId: string | null;
        };
        whatsapp: { status: string; label: string; sentAt?: string | null };
      }>;
    }>(`/yandex/signing-control${qs ? `?${qs}` : ''}`);
    return response.data;
  }

  async getDocument(id: string) {
    const response = await this.client.get<Document>(`/yandex/documents/${id}`);
    return response.data;
  }

  async signDocument(
    id: string,
    data: {
      signature: string;
      certificateThumbprint: string;
      signedAt: string;
      cmsAttached?: string;
    }
  ) {
    const response = await this.client.post(`/yandex/documents/${id}/sign`, data);
    return response.data;
  }

  async matchSignatures(payload: {
    items: Array<{ documentId: string; pdfSha256: string }>;
    signatures: string[];
  }) {
    const response = await this.client.post<{
      matched: Array<{ documentId: string; signatureIndex: number }>;
      errors: Array<{ documentId: string; error: string }>;
      unmatchedSignatureIndices: number[];
    }>('/yandex/documents/match-signatures', payload);
    return response.data;
  }

  async downloadDocument(documentId: string): Promise<void> {
    const doc = await this.getDocument(documentId);
    const b64 = doc.signedPdfBase64 || doc.pdfBase64;
    if (!b64) throw new Error('PDF недоступен');
    const bytes = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
    const blob = new Blob([bytes], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `document-${documentId}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  }

  async downloadZip(documentId: string): Promise<void> {
    const response = await this.client.get(`/yandex/documents/${documentId}/zip`, {
      responseType: 'blob',
    });
    const url = URL.createObjectURL(response.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = `documents_${documentId}.zip`;
    a.click();
    URL.revokeObjectURL(url);
  }

  async getSignLink(documentId: string): Promise<string> {
    const response = await this.client.get<{ deeplink: string }>(
      `/yandex/documents/${documentId}/sign-link`
    );
    return response.data.deeplink;
  }

  async getCompany() {
    const response = await this.client.get<{ company: YandexCompanyProfile }>('/yandex/company');
    return response.data.company;
  }

  async listUsers() {
    const response = await this.client.get<{ users: YandexUserRow[] }>('/yandex/users');
    return response.data.users || [];
  }

  async createUser(data: { name: string; iin: string }) {
    const response = await this.client.post('/yandex/users', data);
    return response.data;
  }

  async listApiClients() {
    const response = await this.client.get<{ clients: ApiClientRow[] }>('/yandex/api-clients');
    return response.data.clients || [];
  }

  async createApiClient(name: string) {
    const response = await this.client.post<ApiClientRow & { apiKey: string; warning?: string }>(
      '/yandex/api-clients',
      { name }
    );
    return response.data;
  }

  async revokeApiClient(id: string) {
    const response = await this.client.delete(`/yandex/api-clients/${id}`);
    return response.data;
  }
}

export const api = new YandexApiService();
